import React, { useEffect, useState } from "react";
import axios from "axios";
import { Bed, Receipt, Info, CheckCircle, Upload, X } from "lucide-react";
import { Button, Card, Input } from "../../shared/ui";
import { uploadReceipt } from "../../lib/uploadReceipt";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** ymd of "today" in the worker's local timezone (AEST in practice). */
const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/**
 * WorkingAwayCard — lets a worker self-flag a date as "working away & staying
 * overnight" (Living Away Allowance) and attach a dinner receipt to that day.
 *
 * Policy is fetched from /api/laa/policy so the FE always shows current rates.
 *
 * Props:
 *   - dateIso: 'YYYY-MM-DD' (defaults to today)
 *   - compact: bool — hide the explainer when embedded inside the timesheet drawer
 *   - onChanged: () => void — fired after a successful toggle/upload
 */
export const WorkingAwayCard = ({ dateIso, compact = false, onChanged }) => {
  const date = dateIso || todayLocal();
  const [policy, setPolicy] = useState(null);
  const [flagged, setFlagged] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);

  // Receipt upload state
  const [showReceiptForm, setShowReceiptForm] = useState(false);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const [savedReceipts, setSavedReceipts] = useState([]);
  const [pickedFile, setPickedFile] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/laa/policy`);
        if (!cancelled) setPolicy(r.data);
      } catch (e) {
        // non-blocking
      }
      try {
        // Detect existing LAA flag + receipts for this date from current week's timesheet
        // (cheap: pull weekly summary which already has reimbursements + entries).
        const tsRes = await axios.get(`${API}/timesheets`);
        const weekTs = (tsRes.data || []).find(t => {
          if (!t.week_start) return false;
          const rows = t.entries || t.original_entries || [];
          return rows.some(e => e.date === date);
        });
        if (weekTs) {
          const row = (weekTs.entries || weekTs.original_entries || []).find(e => e.date === date);
          if (!cancelled && row) setFlagged(!!(row.laa || row.living_away_allowance));
          const dinners = (weekTs.reimbursements || []).filter(r =>
            r.date === date && (r.subtype === "laa_dinner" || (r.expense_type === "meals" && r.description?.toLowerCase().includes("dinner")))
          );
          if (!cancelled) setSavedReceipts(dinners);
        }
      } catch (e) {
        // silent
      }
    })();
    return () => { cancelled = true; };
  }, [date]);

  const toggleLAA = async () => {
    setToggling(true);
    const next = !flagged;
    try {
      await axios.post(`${API}/timesheets/laa-toggle`, { date, laa: next });
      setFlagged(next);
      toast.success(next
        ? `LAA flagged for ${date} — $${policy?.night_rate ?? 40}/night`
        : `LAA removed for ${date}`);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update LAA");
    } finally {
      setToggling(false);
    }
  };

  const saveReceipt = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error("Enter the dinner amount");
      return;
    }
    if (!pickedFile) {
      toast.error("Please attach a photo of your receipt");
      return;
    }
    setUploading(true);
    try {
      const up = await uploadReceipt(pickedFile);
      const r = await axios.post(`${API}/timesheets/laa-dinner-receipt`, {
        date,
        amount: amt,
        receipt_url: up.url,
        description: description || "Dinner — working away",
      });
      if (r.data.capped) {
        toast.success(`Receipt saved — $${r.data.reimbursed_amount} reimbursed (capped from $${r.data.claimed_amount})`);
      } else {
        toast.success(`Receipt saved — $${r.data.reimbursed_amount} reimbursed`);
      }
      setSavedReceipts(prev => [...prev, {
        id: r.data.reimbursement_id,
        date,
        amount: r.data.reimbursed_amount,
        claimed_amount: r.data.claimed_amount,
        capped: r.data.capped,
        description: description || "Dinner — working away",
        receipt_url: up.url,
        status: "pending_approval",
      }]);
      setShowReceiptForm(false);
      setAmount("");
      setDescription("");
      setPickedFile(null);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Receipt upload failed");
    } finally {
      setUploading(false);
    }
  };

  const policyTxt = policy
    ? `$${policy.night_rate} per night + up to $${policy.dinner_cap} dinner (receipt)`
    : "$40/night + up to $30 dinner (receipt)";

  return (
    <Card className={`border-2 ${flagged ? "border-purple-300 bg-purple-50" : "border-slate-200"}`} data-testid="working-away-card">
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${flagged ? "bg-purple-200 text-purple-700" : "bg-slate-100 text-slate-500"}`}>
          <Bed className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <h3 className="font-bold uppercase text-sm tracking-tight">Working away tonight?</h3>
              <p className="text-xs text-slate-500">{compact ? date : `Tonight (${date})`} · {policyTxt}</p>
            </div>
            <Button
              size="sm"
              variant={flagged ? "secondary" : "outline"}
              onClick={toggleLAA}
              disabled={toggling}
              data-testid="laa-toggle-btn"
            >
              {flagged ? (<><CheckCircle className="w-4 h-4 mr-1" /> Flagged</>) : "I'm staying away"}
            </Button>
          </div>

          {!compact && (
            <button
              type="button"
              onClick={() => setShowPolicy(s => !s)}
              className="text-[11px] text-purple-700 hover:underline mt-2 inline-flex items-center gap-1"
              data-testid="laa-policy-toggle"
            >
              <Info className="w-3 h-3" /> How does this work?
            </button>
          )}
          {showPolicy && policy && (
            <div className="mt-2 p-2 bg-white rounded border border-purple-200 text-xs text-slate-700" data-testid="laa-policy-text">
              {policy.explanation}
            </div>
          )}

          {flagged && (
            <div className="mt-3 pt-3 border-t border-purple-200/60">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold uppercase text-slate-500">Dinner receipts for {date}</p>
                {!showReceiptForm && (
                  <Button size="sm" variant="ghost" onClick={() => setShowReceiptForm(true)} data-testid="laa-add-receipt-btn">
                    <Receipt className="w-3.5 h-3.5 mr-1" /> Add dinner receipt
                  </Button>
                )}
              </div>

              {savedReceipts.length > 0 && (
                <div className="space-y-1.5 mb-2">
                  {savedReceipts.map((r) => (
                    <div key={r.id} className="flex items-center gap-2 text-xs bg-white border border-purple-200 rounded p-2" data-testid={`laa-receipt-${r.id}`}>
                      <Receipt className="w-3.5 h-3.5 text-purple-600 flex-shrink-0" />
                      <span className="font-bold text-slate-800">${r.amount?.toFixed(2)}</span>
                      {r.capped && <span className="text-[10px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">capped from ${r.claimed_amount?.toFixed(2)}</span>}
                      <span className="text-slate-500 truncate flex-1">{r.description}</span>
                      {r.receipt_url && (
                        <a href={r.receipt_url} target="_blank" rel="noreferrer" className="text-purple-700 hover:underline">view</a>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {showReceiptForm && (
                <div className="space-y-2 p-3 bg-white border-2 border-purple-300 rounded" data-testid="laa-receipt-form">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] uppercase font-bold text-slate-500">Amount ($)</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="28.50"
                        className="h-9 text-sm"
                        data-testid="laa-receipt-amount"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase font-bold text-slate-500">Description</label>
                      <Input
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Dinner — Hotel restaurant"
                        className="h-9 text-sm"
                        data-testid="laa-receipt-description"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase font-bold text-slate-500">Receipt photo</label>
                    <label className="block mt-1 px-3 py-3 border-2 border-dashed border-purple-300 rounded text-center text-xs text-slate-600 cursor-pointer hover:bg-purple-50">
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        className="hidden"
                        onChange={(e) => setPickedFile(e.target.files?.[0] || null)}
                        data-testid="laa-receipt-file"
                      />
                      {pickedFile ? (
                        <span className="font-semibold text-purple-700"><Upload className="w-3.5 h-3.5 inline mr-1" />{pickedFile.name}</span>
                      ) : (
                        <span><Upload className="w-3.5 h-3.5 inline mr-1" />Tap to pick a photo of the receipt</span>
                      )}
                    </label>
                  </div>
                  <p className="text-[11px] text-slate-500 italic">
                    Reimbursed amount = actual receipt, capped at ${policy?.dinner_cap ?? 30}. (e.g. $28 receipt → $28, $34 receipt → $30)
                  </p>
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => { setShowReceiptForm(false); setPickedFile(null); setAmount(""); setDescription(""); }} disabled={uploading}>
                      <X className="w-3.5 h-3.5 mr-1" /> Cancel
                    </Button>
                    <Button size="sm" onClick={saveReceipt} disabled={uploading} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="laa-receipt-save">
                      {uploading ? "Uploading..." : "Save receipt"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default WorkingAwayCard;
